import axios from 'axios';

const API = axios.create({
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
});

API.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

/**
 * Axios request interceptor. This is called before every request and
 * adds the X-CSRF-TOKEN and X-Socket-Id headers
 */
API.interceptors.request.use(
  config => {
    const token = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute('content');
    let xsocketId = null;
    if (window && window.echo) {
      xsocketId = window.echo.socketId();
    }
    config.headers.common['X-CSRF-TOKEN'] = token;
    if (xsocketId) {
      config.headers.common['X-Socket-Id'] = xsocketId;
    }
    return config;
  },
  error => {
    console.error('API request error:', error);
    return Promise.reject(error);
  }
);

/**
 * Generic error handler that just logs the error
 * @param {Object} error
 */
function handler(error) {
  // User cancelled the Project Upload
  if (error.name === 'CanceledError' && error.message === 'UserCancellation') {
    return Promise.reject(error);
  }
  console.error('API:', error);
  console.error('Response', error.response);
  // In case of a System Unavailable (maintenance) response, reload the page
  if (error.response.status === 503) {
    window.location.reload(true);
  }
  return Promise.reject(error);
}

export default {
  /**
   * Initialize the application
   */
  initialize: async () => API.post('/init/load').catch(handler),

  /**
   * Logout from the app
   */
  logout: () => API.post('/logoutX').catch(handler),

  /**
   * Retrieve the server current time
   */
  getServerTime: async () => API.post('/getServerTime').catch(handler),

  /**
   * projects api namespace
   */
  projects: {
    /**
     * Patch a series of changes to a project
     * @param  {string}  hash    The project hash
     * @param  {array}   value   The patches
     * @param  {number}  version The current version
     */
    patch: ({ hash, value, version, actionStack }) =>
      API.patch(`/project/${hash}/update`, {
        patch: value,
        hash,
        version,
        actionStack,
      }).catch(handler),

    /**
     * Load the list of user projects and their participants
     */
    loadProjects: async () => API.post('/projects/loadList').catch(handler),

    /**
     * Load the data of a specific user project
     * @param {string} hash       The project hash
     * @param {string} routeName  The vue route name
     * @param {string} entryPoint The entrypoint of request
     */
    loadProject: ({ hash, routeName, entryPoint }) =>
      API.post(`/project/${hash}/load`, { hash, routeName, entryPoint }).catch(
        handler
      ),

    /**
     * Update the name of a specific project
     * @param {string} hash     The project hash
     * @param {string} name     The project name
     */
    updateName: ({ hash, name }) =>
      API.post(`/project/${hash}/updateName`, { hash, name }).catch(handler),

    /**
     * Update the public attribute of a specific project
     * @param {string}  hash              The project hash
     * @param {string|null}  publicLink   The publicLink attribute
     */
    updatePublicLink: ({ hash, publicLink }) =>
      API.post(`/project/${hash}/updatePublicLink`, { hash, publicLink }).catch(
        handler
      ),

    /**
     * Create a new project given a specific template
     * @param {string}  template  The template (json filename e.g. 'empty', 'sample' e.t.c.)
     */
    createProject: ({ template }) =>
      API.post('/projects/create', { template }).catch(handler),

    /**
     * Duplicate a project given one
     * @param {string}  hash    The project to duplicate
     */
    duplicateProject: ({ hash }) =>
      API.post(`/project/${hash}/duplicate`, { hash }).catch(handler),

    /**
     * Delete a specific project
     * @param {string} hash     The project hash
     */
    deleteProject: ({ hash }) =>
      API.post(`/project/${hash}/delete`, { hash }).catch(handler),

    /**
     * Requests the upload url info
     */
    generateUploadUrl: workspaceId =>
      API.post(`/workspace/${workspaceId}/projects/generateUploadUrl`).catch(
        handler
      ),

    /**
     * Upload and import a project
     * @param   {object}    projectFile       The project file to be uploaded
     * @param   {Function}  progressCallback  A function to be called to update the upload progress
     */
    uploadProject: ({
      projectFile,
      urlInfo,
      progressCallback,
      cancelToken = null,
    }) => {
      const formData = new FormData();
      const config = {
        headers: {
          'content-type': 'multipart/form-data',
          'Access-Control-Allow-Origin': '*',
        },
        onUploadProgress: progressCallback,
        cancelToken,
      };
      if ('fields' in urlInfo) {
        Object.keys(urlInfo.fields).forEach(field => {
          formData.append(field, urlInfo.fields[field]);
        });
      }
      if (urlInfo.type === 'local') {
        formData.append('projectHash', urlInfo.projectHash);
      }
      formData.append('file', projectFile);
      return API.post(urlInfo.url, formData, config).catch(handler);
    },

    /**
     * Lists the external users of a project
     * @param {string} hash The project hash
     */
    listExternalUsers: ({ hash }) =>
      API.get(`/project/${hash}/externalUsers`)
        .then(res => res.data)
        .catch(handler),

    /**
     * Lists the external users of a project
     * @param {string} hash The project hash
     */
    pendingInvitations: ({ hash }) =>
      API.get(`/project/${hash}/externalUsers/pending-invitations`)
        .then(res => res.data)
        .catch(handler),

    /**
     * Invite a user as an external user in a certain project
     * @param {string} hash The project hash
     * @param {string} email The user's email
     * @param {string} role The role for the invited user
     */
    inviteExternalUser: ({ workspaceId, hash, email, role }) =>
      API.post('/invitations/create', {
        workspace_id: workspaceId,
        hash,
        email,
        role,
      }).catch(handler),

    /**
     * Change an external user's access role
     * @param {string} hash The project hash
     * @param {string} id The user's id
     * @param {string} role The role for the user
     */
    changeExternalUserAccess: ({ hash, id, role }) =>
      API.patch(`/project/${hash}/externalUsers/${id}`, { hash, role })
        .then(res => res.data)
        .catch(handler),

    /**
     * Remove a user from a external users in a certain project
     * @param {string} hash The project hash
     * @param {string} id The user's id
     */
    removeExternalUser: ({ hash, id }) =>
      API.delete(`/project/${hash}/externalUsers/${id}`)
        .then(res => res.data)
        .catch(handler),

    /**
     * Get the number of items in a project (useful in account when we do not have a project loaded)
     * @param {string} hash     The project hash
     */
    loadItemCount: ({ hash }) =>
      API.post(`/project/${hash}/loadItemCount`, { hash }).catch(handler),

    /**
     * This request should become redundant since a project update will now to send the notifications
     */
    createCommentNotification: ({ hash, content }) =>
      API.post(`/project/${hash}/createComment`, { hash, content }).catch(
        handler
      ),

    /**
     * This request should become redundant since a project update will now to send the notifications
     */
    resolveCommentNotification: ({ hash, content }) =>
      API.post(`/project/${hash}/resolveComment`, { hash, content }).catch(
        handler
      ),
  },

  /**
   * assets api namespace
   */
  assets: {
    /**
     * Upload an asset image (base64 string or url) to a specific project
     * @param {string} data     The image (url or base64)
     * @param {string} hash     The project hash
     * @param {object} tiny     The tiny image dimensions
     * @param {object} small    The small image dimensions
     * @param {object} medium   The medium image dimensions
     */
    uploadImage: ({
      data,
      hash,
      tiny = { width: 32, height: 32 },
      small = { width: 240, height: 240 },
      medium = { width: 480, height: 480 },
    }) => {
      const formData = new FormData();
      formData.append('data', data);
      formData.append('tiny', JSON.stringify(tiny));
      formData.append('small', JSON.stringify(small));
      formData.append('medium', JSON.stringify(medium));
      return API.post(`/project/${hash}/assets/uploadImage`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).catch(handler);
    },

    /**
     * Upload an asset image file to a specific project
     * @param {string} hash     The project hash
     * @param {file}   image    The image file data
     * @param {object} tiny     The tiny image dimesions
     * @param {object} small    The small image dimensions
     * @param {object} medium   The medium image dimensions
     */
    uploadImageFile: ({
      hash,
      image,
      tiny = { width: 32, height: 32 },
      small = { width: 240, height: 240 },
      medium = { width: 480, height: 480 },
    }) => {
      const formData = new FormData();
      formData.append('image', image);
      formData.append('tiny', JSON.stringify(tiny));
      formData.append('small', JSON.stringify(small));
      formData.append('medium', JSON.stringify(medium));
      return API.post(`/project/${hash}/assets/uploadImageFile`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).catch(handler);
    },

    /**
     * Upload an asset audio file to a specific project
     * @param {string}  hash    The project hash
     * @param {file}    audio   The audio file data
     */
    uploadAudioFile: ({ hash, audio }) => {
      const formData = new FormData();
      formData.append('audio', audio);
      return API.post(`/project/${hash}/assets/uploadAudioFile`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).catch(handler);
    },

    /**
     * Upload the cover image to a specific project
     * @param {string} data     The image (url or base64)
     * @param {string} hash     The project hash
     */
    uploadCover: ({ data, hash }) => {
      const formData = new FormData();
      formData.append('data', data);
      return API.post(`/project/${hash}/assets/uploadCover`, formData).catch(
        handler
      );
    },

    /**
     * Remove the cover image from a specific project
     * @param {string} hash     The project hash
     * @param {string} file     The cover file name
     * @param {string} type     The cover type
     */
    removeCover: ({ hash, file, type }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      return API.post(`/project/${hash}/assets/removeCover`, formData).catch(
        handler
      );
    },

    /**
     * Get multiple icon svg data into an associative array
     * @param   {array}     icons     The icons
     */
    getIcons: ({ icons }) =>
      API.post('/assets/getIcons', { icons }).catch(handler),

    /**
     * Load the icon metadata and the tag tree
     */
    loadIconMetadata: () => API.post('/assets/loadIconMetadata').catch(handler),
  },

  /**
   * account api namespace
   */
  account: {
    /**
     * Update the user name
     * @param {string}  firstName
     * @param {string}  lastName
     */
    updateName: ({ firstName, lastName }) =>
      API.post('/account/update/name', {
        first_name: firstName,
        last_name: lastName,
      }).catch(handler),

    /**
     * Update the user email
     * @param {string}  email
     * @param {string}  emailConfirmation
     */
    updateEmail: ({ email, emailConfirmation }) =>
      API.post('/account/update/email', {
        email,
        email_confirmation: emailConfirmation,
      }).catch(handler),

    /**
     * Update the user password
     * @param {string}  oldPassword
     * @param {string}  password
     * @param {string}  passwordConfirmation
     */
    updatePassword: ({ oldPassword, password, passwordConfirmation }) =>
      API.post('/account/update/password', {
        old_password: oldPassword,
        password,
        password_confirmation: passwordConfirmation,
      }).catch(handler),

    /**
     * Update the user color
     * @param {string}  color
     */
    updateColor: ({ color }) =>
      API.post('/account/update/color', { color }).catch(handler),

    /**
     * Update the subscibed to newsletter attribute
     * @param {boolean}  value
     */
    updateSubscribedToNewsletter: ({ value }) =>
      API.post('/account/update/subscribed', { value }).catch(handler),

    /**
     * Remove a user's gravatar
     */
    deleteGravatar: () => API.post('/account/deleteGravatar').catch(handler),

    /**
     * Upload a user's avatar
     * @param {string}    image
     */
    uploadAvatar: ({ image }) =>
      API.post('/account/uploadAvatar', { image }).catch(handler),

    /**
     * Remove a user's avatar
     */
    deleteAvatar: () => API.post('/account/deleteAvatar').catch(handler),

    /**
     * Remove a user account
     */
    deleteAccount: () => API.post('/account/deleteAccount').catch(handler),
  },

  /**
   * export api namespace
   */
  export: {
    /**
     * Export a project to specified game engine
     * @param {string}  hash            The project hash
     * @param {string}  engine          The game engine: 'unity', 'godot', 'unreal'
     */
    engine: ({ hash, engine, version = '' }) =>
      API.post(`/project/${hash}/export/engine`, { engine, version }).catch(
        handler
      ),

    /**
     * Export a project to JSON
     * @param {string}  hash            The project hash
     * @param {array}   boards          The boards to be exported
     * @param {boolean} exportAssets    Whether to export the assets with the project
     */
    json: ({ hash, boards, exportAssets }) =>
      API.post(`/project/${hash}/export/json`, { boards, exportAssets }).catch(
        handler
      ),

    /**
     * Export a project to Backup Zip File
     * @param {string}  hash            The project hash
     */
    backup: ({ hash }) =>
      API.post(`/project/${hash}/export/backup`).catch(handler),

    /**
     * Export a project to spreadsheet xlsx
     * @param {string}  hash            The project hash
     * @param {string}  Type            The spreadsheet type: 'xlsx', 'csv'
     */
    spreadsheet: ({ hash, type }) =>
      API.post(`/project/${hash}/export/spreadsheet`, { type }).catch(handler),

    /**
     * Export a project to PDF
     * @param {string}  hash            The project hash
     * @param {array}   board           The board to be exported
     * @param {boolean} printFriendly   Whether we want the print-friendly version
     */
    pdf: ({ hash, board, printFriendly }) =>
      API.post(`/project/${hash}/export/pdf`, {
        board,
        printFriendly,
      }).catch(handler),

    /**
     * Export a project to image
     * @param {string}  hash            The project hash
     * @param {array}   board           The board to be exported
     * @param {boolean} printFriendly   Whether we want the print-friendly version
     */
    image: ({ hash, board, type, size, printFriendly }) =>
      API.post(`/project/${hash}/export/image`, {
        board,
        type,
        size,
        printFriendly,
      }).catch(handler),
  },

  /**
   * Fetch subscription plans list
   */
  subscriptionPlans: () =>
    API.post('/subscription-plans')
      .then(res => res.data)
      .catch(handler),

  /**
   * me api
   */

  me: {
    get: () =>
      API.post('/me')
        .then(res => {
          return res.data.data;
        })
        .catch(handler),

    pendingInvitations: () =>
      API.get('/me/pending-invitations')
        .then(res => {
          return res.data;
        })
        .catch(handler),

    /**
     * Get all the workspace connections of the user
     * @returns {Promise}
     */
    workspaceConnections: workspaceId =>
      API.post('/me/workspace-connections', {
        workspace_id: workspaceId,
      })
        .then(res => res.data)
        .catch(handler),
  },

  /**
   * memberships api
   */
  memberships: {
    /**
     * Update a membership
     */
    update: ({ workspaceId, id, role }) =>
      API.patch(`/workspace/${workspaceId}/memberships/${id}`, { role })
        .then(res => res.data)
        .catch(handler),
    /**
     * Delete a membership based on id
     */
    delete: ({ workspaceId, id }) =>
      API.delete(`/workspace/${workspaceId}/memberships/${id}`)
        .then(res => res.data)
        .catch(handler),
  },

  /**
   * workspaces api
   */
  workspaces: {
    init: ({ workspaceId = null, projectHash = null, workspaceHash }) =>
      API.post('/workspace/init', {
        workspace_id: workspaceId,
        workspace_hash: workspaceHash,
        project_hash: projectHash,
      })
        .then(res => res.data)
        .catch(handler),
    load: (workspaceId = '') =>
      API.get(workspaceId ? `/workspace/${workspaceId}` : '/workspace')
        .then(res => {
          return res.data;
        })
        .catch(handler),
    create: ({ name }) =>
      API.post('/workspace', { name })
        .then(res => res.data)
        .catch(handler),
    update: (workspaceId, payload) =>
      API.patch(`/workspace/${workspaceId}`, payload).catch(handler),
    delete: workspaceId =>
      API.delete(`/workspace/${workspaceId}`)
        .then(res => res.data)
        .catch(handler),
    changeIcon: (workspaceId, { image }) =>
      API.patch(`/workspace/${workspaceId}/icon`, { image }).catch(handler),
    setCurrent: ({ workspaceId = null, projectHash = null }) =>
      API.post('/workspace/setCurrent', {
        workspace_id: workspaceId,
        project_hash: projectHash,
      })
        .then(res => res.data)
        .catch(handler),
    members: workspaceId =>
      API.post(`/workspace/${workspaceId}/members`)
        .then(res => {
          return res.data;
        })
        .catch(handler),
    externalUsers: {
      list: workspaceId =>
        API.get(`/workspace/${workspaceId}/externalUsers`)
          .then(res => res.data)
          .catch(handler),
      remove: (workspaceId, { userId }) =>
        API.delete(`/workspace/${workspaceId}/externalUsers/${userId}`)
          .then(res => res.data)
          .catch(handler),
    },
    subscription: {
      /**
       * Create a new workspace subscription
       * @param {Object}  data            The subscription data
       */
      create: async (workspaceId, data) =>
        API.post(`/workspace/${workspaceId}/subscription/create`, data).catch(
          handler
        ),

      /**
       * Validate a discount coupon
       * @param {string}  coupon    The customer facing promotional code for this coupon
       */
      validateCoupon: async (workspaceId, { coupon }) =>
        API.post(`/workspace/${workspaceId}/subscription/validate/coupon`, {
          coupon,
        })
          .then(res => {
            return res.data;
          })
          .catch(handler),

      /**
       * Cancel an existing user subscription
       * @param {Array<string>} reasons The reasons for cancelling
       */
      cancel: async (workspaceId, { reasons }) =>
        API.post(`/workspace/${workspaceId}/subscription/cancel`, { reasons })
          .then(res => {
            return res.data;
          })
          .catch(handler),

      /**
       * Resume the latest cancelled user subscription.
       */
      resumeSubscription: workspaceId =>
        API.post(`/workspace/${workspaceId}/subscription/resume`)
          .then(res => {
            return res.data;
          })
          .catch(handler),

      /**
       * Validate whether the vat is correct
       * @param {string}  vat
       */
      validateVat: async (workspaceId, { vat }) =>
        API.post(`/workspace/${workspaceId}/subscription/validate/vat`, { vat })
          .then(res => {
            return res.data;
          })
          .catch(error => {
            return error;
          }),

      /*
       * Change the subscription plan
       * @param {string} priceId
       */
      changePlan: async (workspaceId, { priceId }) =>
        API.post(`/workspace/${workspaceId}/subscription/change-plan`, {
          priceId,
        })
          .then(res => {
            return res.data;
          })
          .catch(handler),

      /**
       * Get the current workspace's invoices
       */
      invoices: workspaceId =>
        API.post(`/workspace/${workspaceId}/subscription/invoices`)
          .then(res => res.data)
          .catch(handler),

      /**
       * Get the workspace's billing details
       */
      billingInfo: workspaceId =>
        API.post(`/workspace/${workspaceId}/subscription/billingInfo`)
          .then(res => res.data)
          .catch(handler),

      /**
       * Get info on the upcoming invoice
       */
      nextInvoices: workspaceId =>
        API.post(`/workspace/${workspaceId}/subscription/nextInvoices`)
          .then(res => res.data)
          .catch(handler),

      /**
       * Get proration
       */
      proration: async (workspaceId, { priceId }) =>
        API.post(`/workspace/${workspaceId}/subscription/proration`, {
          priceId,
        })
          .then(res => res.data)
          .catch(handler),

      /**
       * Update the invoice details of the current subscription
       * @param {Number} workspaceId
       * @param {*} details
       * @returns {Promise}
       */
      updateInvoiceDetails: async (workspaceId, details) =>
        API.patch(
          `/workspace/${workspaceId}/subscription/invoiceDetails`,
          details
        )
          .then(res => res.data)
          .catch(handler),

      /**
       * Updates the payment method of the current workspace
       * @param {Object} data
       * @param {string} data.stripePaymentMethod
       * @returns
       */
      updatePaymentMethod: async (workspaceId, data) =>
        API.patch(`/workspace/${workspaceId}/subscription/paymentMethod`, data)
          .then(res => res.data)
          .catch(handler),

      getIntent: async workspaceId =>
        API.post(`/workspace/${workspaceId}/subscription/getIntent`)
          .then(res => res.data)
          .catch(handler),
    },

    projects: {
      list: async ({ workspaceId, query = null } = {}) => {
        let options = null;
        if (query) {
          options = { params: { query } };
        }
        return API.get(`/workspace/${workspaceId}/projects`, options)
          .then(res => {
            return res.data;
          })
          .catch(handler);
      },
      /**
       * Create a new project given a specific template
       * @param {string}  template  The template (json filename e.g. 'empty', 'sample' e.t.c.)
       */
      create: ({ workspaceId, template }) =>
        API.post(`/workspace/${workspaceId}/projects/create`, { template })
          .then(res => {
            return res.data;
          })
          .catch(handler),
    },

    apiTokens: {
      list: ({ workspaceId }) =>
        API.post(`/workspace/${workspaceId}/apiTokens`)
          .then(res => res.data)
          .catch(handler),
      create: ({ workspaceId, name }) =>
        API.post(`/workspace/${workspaceId}/apiTokens/create`, { name }).catch(
          handler
        ),
      delete: ({ workspaceId, id }) =>
        API.delete(`/workspace/${workspaceId}/apiTokens/${id}`).catch(handler),
      update: ({ workspaceId, id, name }) =>
        API.patch(`/workspace/${workspaceId}/apiTokens/${id}`, { name })
          .then(res => res.data)
          .catch(handler),
    },

    ai: {
      aiElementGenerator: async ({ workspaceId, hash, image, data }) =>
        API.post(`/workspace/${workspaceId}/api/ai-element-generator`, {
          hash,
          image,
          data,
        }).catch(handler),

      aiElementEnhancer: async ({ workspaceId, hash, image, data }) =>
        API.post(`/workspace/${workspaceId}/api/ai-element-enhancer`, {
          hash,
          image,
          data,
        }).catch(handler),
    },
  },

  /**
   * roles api
   */
  roles: {
    get: workspaceId =>
      API.post(`/workspace/${workspaceId}/roles`)
        .then(res => {
          return res.data;
        })
        .catch(handler),
    create: (workspaceId, payload) =>
      API.post(`/workspace/${workspaceId}/roles/create`, payload)
        .then(res => {
          return res.data;
        })
        .catch(handler),
    read: (workspaceId, { id }) =>
      API.get(`/workspace/${workspaceId}/roles/${id}`)
        .then(res => {
          return res.data;
        })
        .catch(handler),
    update: (workspaceId, { id, payload }) =>
      API.patch(`/workspace/${workspaceId}/roles/${id}`, payload)
        .then(res => {
          return res.data;
        })
        .catch(handler),
    delete: (workspaceId, { id }) =>
      API.delete(`/workspace/${workspaceId}/roles/${id}`)
        .then(res => {
          return res.data;
        })
        .catch(handler),
  },

  /**
   * permissions api
   */
  permissions: {
    get: () =>
      API.get('/permissions')
        .then(res => {
          return res.data;
        })
        .catch(handler),
    current: ({ workspaceId = null, projectHash = null } = {}) =>
      API.post('/permissions', {
        workspace_id: workspaceId,
        project_hash: projectHash,
      })
        .then(res => res.data)
        .catch(handler),
  },

  features: {
    current: ({ workspaceId = null, projectHash = null } = {}) =>
      API.post('/features', {
        workspace_id: workspaceId,
        project_hash: projectHash,
      })
        .then(res => res.data)
        .catch(handler),
  },

  /**
   * invitations api
   */
  invitations: {
    get: (workspaceId, { status }) =>
      API.get(`/workspace/${workspaceId}/invitations`, { params: { status } })
        .then(res => {
          return res.data;
        })
        .catch(handler),
    create: payload =>
      API.post('/invitations/create', payload)
        .then(res => {
          return res.data;
        })
        .catch(handler),
    accept: ({ id }) => {
      return API.post(`/invitations/${id}/accept`)
        .then(res => {
          return res.data;
        })
        .catch(handler);
    },
    decline: ({ id }) => {
      return API.post(`/invitations/${id}/decline`)
        .then(res => res.data)
        .catch(handler);
    },
    delete: ({ id }) =>
      API.delete(`/invitations/${id}`)
        .then(res => {
          return res.data;
        })
        .catch(handler),
    resend: ({ id }) =>
      API.post(`/invitations/${id}/resend`)
        .then(res => {
          return res.data;
        })
        .catch(handler),
    update: ({ id, roleId }) =>
      API.patch(`/invitations/${id}`, { role_id: roleId })
        .then(res => {
          return res.data;
        })
        .catch(handler),
  },
};
